/**
 * useArchitectRunStream — subscribes to /ws/architect/runs/:runId/events
 * and invalidates the per-run TanStack Query cache on every NOTIFY.
 *
 * The hook is "fire and forget" — it returns the connected boolean for
 * status display but does NOT keep an in-memory copy of nodes. The
 * canonical view comes from `architectKeys.run(runId)`; we just nudge
 * the cache when the gateway reports a state change.
 *
 * Reconnect strategy mirrors useBoardEvents.
 */
import type { ArchitectWsEvent } from '@chaos/types/architect';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { env } from '../../env';
import { architectKeys } from '../api/architect';

function wsUrl(runId: string): string {
  const base = env.VITE_API_BASE_URL;
  let url: URL;
  try {
    url = new URL(base, window.location.origin);
  } catch {
    url = new URL(window.location.origin);
  }
  const proto = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${url.host}/ws/architect/runs/${runId}/events`;
}

interface UseArchitectRunStreamOptions {
  enabled?: boolean;
  url?: string;
  WebSocketCtor?: typeof WebSocket;
}

export function useArchitectRunStream(
  runId: string | undefined,
  opts: UseArchitectRunStreamOptions = {},
): { connected: boolean } {
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUs = useRef(false);
  const attemptRef = useRef(0);
  const [connected, setConnected] = useState(false);

  const enabled = opts.enabled !== false && Boolean(runId);
  const overrideUrl = opts.url;
  const Ctor = opts.WebSocketCtor;

  useEffect(() => {
    if (!runId || !enabled) return;

    const handleFrame = (raw: string): void => {
      let frame: ArchitectWsEvent;
      try {
        frame = JSON.parse(raw) as ArchitectWsEvent;
      } catch {
        return;
      }
      if (frame.kind === 'ping' || frame.kind === 'hello') return;
      // Either run or node update — nudge the run detail cache.
      qc.invalidateQueries({ queryKey: architectKeys.run(runId) });
      // Also invalidate the history table — status may have changed.
      qc.invalidateQueries({ queryKey: architectKeys.all });
    };

    const connect = (): void => {
      const Constructor = Ctor ?? WebSocket;
      const url = overrideUrl ?? wsUrl(runId);
      const ws = new Constructor(url);
      wsRef.current = ws;

      ws.onopen = () => {
        attemptRef.current = 0;
        setConnected(true);
      };
      ws.onmessage = (msgEvent) => {
        const data =
          typeof msgEvent.data === 'string'
            ? msgEvent.data
            : new TextDecoder().decode(msgEvent.data as ArrayBuffer);
        handleFrame(data);
      };
      ws.onclose = () => {
        wsRef.current = null;
        setConnected(false);
        if (closedByUs.current) return;
        const delay = Math.min(30_000, 1_000 * 2 ** attemptRef.current);
        attemptRef.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        // onclose follows.
      };
    };

    closedByUs.current = false;
    connect();

    return () => {
      closedByUs.current = true;
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
      try {
        wsRef.current?.close();
      } catch {
        // Already closed.
      }
      wsRef.current = null;
    };
  }, [runId, enabled, overrideUrl, Ctor, qc]);

  return { connected };
}
